// lib/db/schema.ts — IndexedDB schema using idb library
// Schema version 1

import { openDB } from "idb";
import type { DBSchema, IDBPDatabase } from 'idb';
import type {
  Group, PlannedTransaction, AdHocTransaction, RecurringTransaction,
  MonthlySnapshot, AllocationHistory, SyncLog, PinState
} from '../../types';

export const SCHEMA_VERSION = 1;
export const DB_NAME = 'sortednow';

interface SortednowDB extends DBSchema {
  groups: {
    key: string; // groupId
    value: Group;
    indexes: {
      'by-userId': string;
      'by-createdAt': string;
    };
  };
  transactions: {
    key: string; // transactionId
    value: PlannedTransaction | AdHocTransaction | RecurringTransaction;
    indexes: {
      'by-groupId': string;
      'by-userId': string;
      'by-type': string;
      'by-status': string;
      'by-dueDate': string;
    };
  };
  monthlySnapshots: {
    key: [string, string]; // [userId, yearMonth]
    value: MonthlySnapshot;
    indexes: {
      'by-userId': string;
    };
  };
  allocationHistory: {
    key: string; // historyId
    value: AllocationHistory;
    indexes: {
      'by-transactionId': string;
      'by-userId': string;
    };
  };
  syncLog: {
    key: string; // syncId
    value: SyncLog;
    indexes: {
      'by-userId': string;
      'by-provider': string;
    };
  };
  // Local auth storage
  pinState: {
    key: string; // userId
    value: PinState & { userId: string };
  };
  // App settings per user
  settings: {
    key: string; // userId
    value: {
      userId: string;
      onboardingComplete: boolean;
      autoSync: boolean;
      notifyUpcomingBills: boolean;
      notifyAllocationChanges: boolean;
      notifySyncStatus: boolean;
      currency: string;
      timezone: string;
      driveConnected?: boolean;
      driveEmail?: string | null;
      driveName?: string | null;
      pendingSettingsSync?: boolean;
    };
  };
}

let dbInstance: IDBPDatabase<SortednowDB> | null = null;

export async function getDB(): Promise<IDBPDatabase<SortednowDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<SortednowDB>(DB_NAME, SCHEMA_VERSION, {
    upgrade(db) {
      // groups
      if (!db.objectStoreNames.contains('groups')) {
        const groups = db.createObjectStore('groups', { keyPath: 'groupId' });
        groups.createIndex('by-userId', 'userId');
        groups.createIndex('by-createdAt', 'createdAt');
      }

      // transactions
      if (!db.objectStoreNames.contains('transactions')) {
        const txns = db.createObjectStore('transactions', { keyPath: 'transactionId' });
        txns.createIndex('by-groupId', 'groupId');
        txns.createIndex('by-userId', 'userId');
        txns.createIndex('by-type', 'type');
        txns.createIndex('by-status', 'status');
        txns.createIndex('by-dueDate', 'dueDate');
      }

      // monthlySnapshots
      if (!db.objectStoreNames.contains('monthlySnapshots')) {
        const snaps = db.createObjectStore('monthlySnapshots', { keyPath: ['userId', 'yearMonth'] });
        snaps.createIndex('by-userId', 'userId');
      }

      // allocationHistory
      if (!db.objectStoreNames.contains('allocationHistory')) {
        const hist = db.createObjectStore('allocationHistory', { keyPath: 'historyId' });
        hist.createIndex('by-transactionId', 'transactionId');
        hist.createIndex('by-userId', 'userId');
      }

      // syncLog
      if (!db.objectStoreNames.contains('syncLog')) {
        const sync = db.createObjectStore('syncLog', { keyPath: 'syncId' });
        sync.createIndex('by-userId', 'userId');
        sync.createIndex('by-provider', 'provider');
      }

      // pinState
      if (!db.objectStoreNames.contains('pinState')) {
        db.createObjectStore('pinState', { keyPath: 'userId' });
      }

      // settings
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'userId' });
      }
    },
    blocked() {
      console.warn('[DB] Database upgrade blocked — close other tabs');
    },
  });

  return dbInstance;
}
